import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft } from "lucide-react";

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const linksRef = useRef(null);

  const handleLogout = async () => {
    try {
      const success = await logout();
      if (success) {
        navigate("/");
      }
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      setMobileOpen(false);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        linksRef.current &&
        !linksRef.current.contains(e.target) &&
        !e.target.closest(".navbar-toggle") &&
        mobileOpen
      ) {
        setMobileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileOpen]);

  // Close mobile menu when changing routes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks =
    user?.role === "admin" && isAuthenticated
      ? [
          { to: "/admin/dashboard", label: "Dashboard" },
          { to: "/admin/clients/add", label: "Add Client" },
          { to: "/admin/transactions-ledger", label: "View All Transactions" },
          { to: "/fd-calculator", label: "FD Calculator" },
        ]
      : [
          { to: `/${user ? user.userId : "dashboard"}`, label: "Profile" },
          { to: "/my-account", label: "My Account" },
          { to: "/fd-calculator", label: "FD Calculator" },
          { to: "/apply", label: "Apply" },
          { to: "/transactions-ledger", label: "Transactions Ledger" },
        ];

  const isHomePage =
    location.pathname === "/" ||
    (user?.role === "admin" && location.pathname === "/admin/dashboard") ||
    (user?.role === "user" && location.pathname === `/${user?.userId}`);

  return (
    <header className={`navbar-container`}>
      <div className="navbar-content">
        <div className="navbar-start flex items-center">
          {!isHomePage && (
            <button
              onClick={goBack}
              className="navbar-back-btn flex items-center"
              aria-label="Go back"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
          )}

          <Link to="/" className="navbar-logo">
            Azad Finance
          </Link>
        </div>

        <button
          className={`navbar-toggle ${mobileOpen ? "open" : ""}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>

        <nav
          ref={linksRef}
          className={`navbar-links ${mobileOpen ? "active" : ""}`}
        >
          <div className="flex justify-between w-full">
            <div className="navbar-center">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`navbar-link ${
                    location.pathname === link.to ? "active" : ""
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="navbar-end">
              {isAuthenticated && (
                <span className="navbar-welcome">
                  {user?.name || "User"}'s Account
                </span>
              )}

              {isAuthenticated ? (
                <button onClick={handleLogout} className="navbar-button">
                  Logout
                </button>
              ) : (
                <button
                  onClick={() => navigate("/login")}
                  className="navbar-button login-btn"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
